import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut, FileText, Download, CreditCard, Building, Wallet, Languages, FileSignature, AlertCircle, Settings, FolderOpen, TrendingUp, Copy, Check } from "lucide-react";
import SignatureCapture from "@/components/SignatureCapture";
import ClientProfile from "./ClientProfile";

// Translation dictionaries
const translations = {
  en: {
    propertyManagement: "Property Management Portal",
    logout: "Logout",
    yourMonthlyReports: "Your Monthly Reports",
    accessReports: "Access your property reservation reports and payment information",
    loading: "Loading your files...",
    noFiles: "No files available yet. Your reports will appear here once uploaded.",
    reports: "reports",
    report: "report",
    available: "available",
    paid: "Paid",
    paymentPending: "Payment Pending",
    importantNotice: "Important Notice",
    reservationReport: "Reservation Report",
    downloadReport: "Download Report",
    ltdDocuments: "LTD Documents",
    lpDocuments: "LP Documents",
    takkPdf: "TAKK PDF",
    downloadTakk: "Download TAKK",
    markAsPaid: "Mark as Paid",
    invoicePdf: "Invoice PDF",
    downloadInvoice: "Download Invoice",
    otherDocuments: "Other Documents",
    downloadDocument: "Download",
    paymentInformation: "Payment Information",
    payNowWithStripe: "Pay Now with Stripe",
    bankTransferIban: "Bank Transfer (IBAN)",
    revolut: "Revolut",
    wiseTransfer: "Wise Transfer",
    paymentReceived: "Payment received. Thank you!",
    takkMarkedPaid: "TAKK marked as paid!",
    invoiceMarkedPaid: "Invoice marked as paid!",
    failedMarkPaid: "Failed to mark as paid",
    filterByYear: "Filter by Year",
    filterByMonth: "Filter by Month",
    allYears: "All Years",
    allMonths: "All Months",
    lastUpload: "Last upload",
    // Tabs
    supportiveDocuments: "Supportive Documents",
    // Profile & Settings
    profile: "Profile",
    settings: "Settings",
    // Contract translations
    contractDetailsRequired: "Contract Details Required",
    completeContractPrompt: "Please complete your contract details and sign the agreement. This is required before accessing other services.",
    completeContractNow: "Complete Contract Now",
    completeYourContract: "Complete Your Contract",
    fillDetailsAndSign: "Please fill in your details and sign the contract",
    downloadEmptyTemplate: "Download Empty Template",
    emptyTemplateNote: "Note: This is a blank template with placeholders. Fill in your information when submitting the contract.",
    yourSignature: "Your Signature",
    signatureType: "Signature Type",
    typeSignature: "Type Signature",
    drawSignature: "Draw Signature",
    typeYourName: "Type your full name",
    drawYourSignature: "Draw your signature",
    clearSignature: "Clear",
    submitContract: "Submit Contract",
    submitting: "Submitting...",
    contractSubmittedSuccess: "Contract submitted successfully! You will receive a copy via email.",
    contractSubmitFailed: "Failed to submit contract",
    // Supportive Documents translations
    accessHelpfulResources: "Access helpful resources and documentation",
    loadingDocuments: "Loading documents...",
    noDocumentsAvailable: "No documents available at the moment",
    availableFiles: "Available Files:",
    downloadFile: "Download",
    // Month names
    january: "January",
    february: "February",
    march: "March",
    april: "April",
    may: "May",
    june: "June",
    july: "July",
    august: "August",
    september: "September",
    october: "October",
    november: "November",
    december: "December",
    // Client income
    yourIncome: "Your Income",
    netIncome: "Net Income",
    totalIncome: "Total Income",
    viewingPeriod: "Viewing Period",
    // Copy functionality
    copy: "Copy",
    copied: "Copied!",
    copiedToClipboard: "Copied to clipboard",
    // Bank details
    beneficiary: "Beneficiary",
    bankName: "Bank Name",
    bic: "BIC / SWIFT",
    or: "Or"
  },
  el: {
    propertyManagement: "Πύλη Διαχείρισης Ακινήτων",
    logout: "Αποσύνδεση",
    yourMonthlyReports: "Οι Μηνιαίες Αναφορές Σας",
    accessReports: "Πρόσβαση στις αναφορές κρατήσεων και πληροφορίες πληρωμών",
    loading: "Φόρτωση των αρχείων σας...",
    noFiles: "Δεν υπάρχουν ακόμη αρχεία. Οι αναφορές σας θα εμφανιστούν εδώ μόλις μεταφορτωθούν.",
    reports: "αναφορές",
    report: "αναφορά",
    available: "διαθέσιμες",
    paid: "Πληρώθηκε",
    paymentPending: "Εκκρεμής Πληρωμή",
    importantNotice: "Σημαντική Ανακοίνωση",
    reservationReport: "Αναφορά Κρατήσεων",
    downloadReport: "Λήψη Αναφοράς",
    ltdDocuments: "Έγγραφα LTD",
    lpDocuments: "Έγγραφα LP",
    takkPdf: "TAKK PDF",
    downloadTakk: "Λήψη TAKK",
    markAsPaid: "Σήμανση ως Πληρωμένο",
    invoicePdf: "Τιμολόγιο PDF",
    downloadInvoice: "Λήψη Τιμολογίου",
    otherDocuments: "Άλλα Έγγραφα",
    downloadDocument: "Λήψη",
    paymentInformation: "Πληροφορίες Πληρωμής",
    payNowWithStripe: "Πληρωμή με Stripe",
    bankTransferIban: "Τραπεζική Μεταφορά (IBAN)",
    revolut: "Revolut",
    wiseTransfer: "Μεταφορά Wise",
    paymentReceived: "Η πληρωμή ελήφθη. Ευχαριστούμε!",
    takkMarkedPaid: "Το TAKK σημειώθηκε ως πληρωμένο!",
    invoiceMarkedPaid: "Το τιμολόγιο σημειώθηκε ως πληρωμένο!",
    failedMarkPaid: "Αποτυχία σήμανσης ως πληρωμένο",
    filterByYear: "Φίλτρο ανά Έτος",
    filterByMonth: "Φίλτρο ανά Μήνα",
    allYears: "Όλα τα Έτη",
    allMonths: "Όλοι οι Μήνες",
    lastUpload: "Τελευταία μεταφόρτωση",
    // Tabs
    supportiveDocuments: "Υποστηρικτικά Έγγραφα",
    // Profile & Settings
    profile: "Προφίλ",
    settings: "Ρυθμίσεις",
    // Contract translations
    contractDetailsRequired: "Απαιτούνται Λεπτομέρειες Σύμβασης",
    completeContractPrompt: "Παρακαλούμε συμπληρώστε τα στοιχεία της σύμβασής σας και υπογράψτε τη συμφωνία. Αυτό απαιτείται πριν από την πρόσβαση σε άλλες υπηρεσίες.",
    completeContractNow: "Ολοκληρώστε τη Σύμβαση Τώρα",
    completeYourContract: "Ολοκληρώστε τη Σύμβασή Σας",
    fillDetailsAndSign: "Παρακαλούμε συμπληρώστε τα στοιχεία σας και υπογράψτε τη σύμβαση",
    downloadEmptyTemplate: "Λήψη Κενού Προτύπου",
    emptyTemplateNote: "Σημείωση: Αυτό είναι ένα κενό πρότυπο με θέσεις κράτησης. Συμπληρώστε τις πληροφορίες σας κατά την υποβολή της σύμβασης.",
    yourSignature: "Η Υπογραφή Σας",
    signatureType: "Τύπος Υπογραφής",
    typeSignature: "Πληκτρολογημένη Υπογραφή",
    drawSignature: "Σχεδιασμένη Υπογραφή",
    typeYourName: "Πληκτρολογήστε το πλήρες όνομά σας",
    drawYourSignature: "Σχεδιάστε την υπογραφή σας",
    clearSignature: "Καθαρισμός",
    submitContract: "Υποβολή Σύμβασης",
    submitting: "Υποβολή...",
    contractSubmittedSuccess: "Η σύμβαση υποβλήθηκε με επιτυχία! Θα λάβετε ένα αντίγραφο μέσω email.",
    contractSubmitFailed: "Αποτυχία υποβολής σύμβασης",
    // Supportive Documents translations
    accessHelpfulResources: "Πρόσβαση σε χρήσιμους πόρους και τεκμηρίωση",
    loadingDocuments: "Φόρτωση εγγράφων...",
    noDocumentsAvailable: "Δεν υπάρχουν διαθέσιμα έγγραφα αυτή τη στιγμή",
    availableFiles: "Διαθέσιμα Αρχεία:",
    downloadFile: "Λήψη",
    // Month names
    january: "Ιανουάριος",
    february: "Φεβρουάριος",
    march: "Μάρτιος",
    april: "Απρίλιος",
    may: "Μάιος",
    june: "Ιούνιος",
    july: "Ιούλιος",
    august: "Αύγουστος",
    september: "Σεπτέμβριος",
    october: "Οκτώβριος",
    november: "Νοέμβριος",
    december: "Δεκέμβριος",
    // Client income
    yourIncome: "Το Εισόδημά Σας",
    netIncome: "Καθαρό Εισόδημα",
    totalIncome: "Συνολικό Εισόδημα",
    viewingPeriod: "Περίοδος Προβολής",
    // Copy functionality
    copy: "Αντιγραφή",
    copied: "Αντιγράφηκε!",
    copiedToClipboard: "Αντιγράφηκε στο πρόχειρο",
    // Bank details
    beneficiary: "Δικαιούχος",
    bankName: "Όνομα Τράπεζας",
    bic: "BIC / SWIFT",
    or: "Ή"
  },
  he: {
    propertyManagement: "פורטל ניהול נכסים",
    logout: "התנתק",
    yourMonthlyReports: "הדוחות החודשיים שלך",
    accessReports: "גישה לדוחות הזמנות ומידע על תשלומים",
    loading: "טוען את הקבצים שלך...",
    noFiles: "אין עדיין קבצים זמינים. הדוחות שלך יופיעו כאן לאחר ההעלאה.",
    reports: "דוחות",
    report: "דוח",
    available: "זמינים",
    paid: "שולם",
    paymentPending: "תשלום ממתין",
    importantNotice: "הודעה חשובה",
    reservationReport: "דוח הזמנות",
    downloadReport: "הורד דוח",
    ltdDocuments: "מסמכי LTD",
    lpDocuments: "מסמכי LP",
    takkPdf: "TAKK PDF",
    downloadTakk: "הורד TAKK",
    markAsPaid: "סמן כשולם",
    invoicePdf: "חשבונית PDF",
    downloadInvoice: "הורד חשבונית",
    otherDocuments: "מסמכים אחרים",
    downloadDocument: "הורד",
    paymentInformation: "מידע תשלום",
    payNowWithStripe: "שלם עכשיו עם Stripe",
    bankTransferIban: "העברה בנקאית (IBAN)",
    revolut: "Revolut",
    wiseTransfer: "העברת Wise",
    paymentReceived: "התשלום התקבל. תודה!",
    takkMarkedPaid: "TAKK סומן כשולם!",
    invoiceMarkedPaid: "החשבונית סומנה כשולמה!",
    failedMarkPaid: "נכשל בסימון כשולם",
    filterByYear: "סינון לפי שנה",
    filterByMonth: "סינון לפי חודש",
    allYears: "כל השנים",
    allMonths: "כל החודשים",
    lastUpload: "העלאה אחרונה",
    // Tabs
    supportiveDocuments: "מסמכי תמיכה",
    // Profile & Settings
    profile: "פרופיל",
    settings: "הגדרות",
    // Contract translations
    contractDetailsRequired: "נדרשים פרטי חוזה",
    completeContractPrompt: "אנא השלם את פרטי החוזה שלך וחתום על ההסכם. זה נדרש לפני גישה לשירותים אחרים.",
    completeContractNow: "השלם חוזה עכשיו",
    completeYourContract: "השלם את החוזה שלך",
    fillDetailsAndSign: "אנא מלא את הפרטים שלך וחתום על החוזה",
    downloadEmptyTemplate: "הורד תבנית ריקה",
    emptyTemplateNote: "הערה: זוהי תבנית ריקה עם מציינים. מלא את המידע שלך בעת הגשת החוזה.",
    yourSignature: "החתימה שלך",
    signatureType: "סוג חתימה",
    typeSignature: "חתימה מוקלדת",
    drawSignature: "צייר חתימה",
    typeYourName: "הקלד את שמך המלא",
    drawYourSignature: "צייר את החתימה שלך",
    clearSignature: "נקה",
    submitContract: "הגש חוזה",
    submitting: "שולח...",
    contractSubmittedSuccess: "החוזה הוגש בהצלחה! תקבל עותק באימייל.",
    contractSubmitFailed: "הגשת החוזה נכשלה",
    // Supportive Documents translations
    accessHelpfulResources: "גישה למשאבים ותיעוד מועילים",
    loadingDocuments: "טוען מסמכים...",
    noDocumentsAvailable: "אין מסמכים זמינים כרגע",
    availableFiles: "קבצים זמינים:",
    downloadFile: "הורד",
    // Month names
    january: "ינואר",
    february: "פברואר",
    march: "מרץ",
    april: "אפריל",
    may: "מאי",
    june: "יוני",
    july: "יולי",
    august: "אוגוסט",
    september: "ספטמבר",
    october: "אוקטובר",
    november: "נובמבר",
    december: "דצמבר",
    // Client income
    yourIncome: "ההכנסה שלך",
    netIncome: "הכנסה נטו",
    totalIncome: "סך הכנסה",
    viewingPeriod: "תקופת צפייה",
    // Copy functionality
    copy: "העתק",
    copied: "הועתק!",
    copiedToClipboard: "הועתק ללוח",
    // Bank details
    beneficiary: "מוטב",
    bankName: "שם הבנק",
    bic: "BIC / SWIFT",
    or: "או"
  }
};

const ClientDashboard = ({ user, onLogout }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("reports");
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('clientLanguage') || 'en';
  });
  
  // Filter state - will be set to most recent year after files are loaded
  const [filterYear, setFilterYear] = useState("0");
  
  // Contract state
  const [assignedTemplate, setAssignedTemplate] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [submittedContracts, setSubmittedContracts] = useState([]);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [signatureType, setSignatureType] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  
  // Supportive Documents state
  const [supportiveTopics, setSupportiveTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  
  // Payment Settings state
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const t = (key) => translations[language][key] || translations.en[key];

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    localStorage.setItem('clientLanguage', newLang);
  };

  useEffect(() => {
    fetchFiles();
    fetchAssignedContract();
    fetchSubmittedContracts();
    fetchSupportiveTopics();
    fetchPaymentSettings();
  }, []);

  const getToken = () => localStorage.getItem('token');

  const fetchFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/files`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
        
        // Set default filter to most recent month with files
        if (data.length > 0) {
          // Sort files to find the most recent
          const sortedFiles = [...data].sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return b.month - a.month;
          });
          const mostRecent = sortedFiles[0];
          setFilterYear(mostRecent.year.toString());
        }
      } else {
        toast.error('Failed to fetch files');
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedContract = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/assigned-contract`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedTemplate(data.template);
      }
    } catch (error) {
      console.error('Error fetching assigned contract:', error);
    }
  };

  const fetchFormConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/form-config`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormConfig(data);
      }
    } catch (error) {
      console.error('Error fetching form config:', error);
    }
  };

  const fetchSubmittedContracts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/contracts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubmittedContracts(data);
      }
    } catch (error) {
      console.error('Error fetching submitted contracts:', error);
    }
  };

  const fetchSupportiveTopics = async () => {
    setLoadingTopics(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/supportive-documents/topics`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSupportiveTopics(data);
      }
    } catch (error) {
      console.error('Error fetching supportive topics:', error);
    } finally {
      setLoadingTopics(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/payment-settings`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentSettings(data);
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const downloadSupportiveFile = async (fileId, fileName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/client/supportive-documents/files/${fileId}/download`,
        {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('File downloaded successfully!');
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const getFileIcon = (fileType) => {
    const icons = {
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'xls': '📊',
      'xlsx': '📊',
      'zip': '🗜️',
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
    };
    return icons[fileType?.toLowerCase()] || '📁';
  };

  const copyToClipboard = (text, fieldId) => {
    if (!text) {
      toast.error('No text to copy');
      return;
    }

    // Remove spaces ONLY for IBAN fields
    const isIbanField = fieldId.includes('iban-') || fieldId.includes('iban_');
    const cleanedText = isIbanField ? text.replace(/\s+/g, '') : text;
    
    // Use the more reliable textarea method (works in all contexts)
    const textArea = document.createElement('textarea');
    textArea.value = cleanedText;
    
    // Make textarea invisible but still accessible
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setCopiedField(fieldId);
        toast.success(t('copiedToClipboard'));
        
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setCopiedField(null);
        }, 2000);
      } else {
        toast.error('Failed to copy - please copy manually');
      }
    } catch (err) {
      document.body.removeChild(textArea);
      console.error('Copy error:', err);
      toast.error('Failed to copy - please copy manually');
    }
  };

  const extractPlaceholders = (templateContent) => {
    // Extract all placeholders like {FirstName}, {LastName}, etc.
    const regex = /\{([^}]+)\}/g;
    const matches = templateContent.matchAll(regex);
    const placeholders = new Set();
    
    for (const match of matches) {
      placeholders.add(match[1]);
    }
    
    return Array.from(placeholders);
  };

  const downloadEmptyTemplate = () => {
    if (!assignedTemplate) {
      toast.error('No template available');
      return;
    }
    
    try {
      // Create a well-formatted HTML document with the template
      const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${assignedTemplate.name}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      margin: 40px;
      line-height: 1.6;
      color: #333;
    }
    h1 {
      color: #1a56db;
      border-bottom: 2px solid #1a56db;
      padding-bottom: 10px;
      margin-bottom: 30px;
    }
    .template-header {
      background: #f3f4f6;
      padding: 15px;
      border-radius: 5px;
      margin-bottom: 30px;
    }
    .content {
      margin: 20px 0;
    }
  </style>
</head>
<body>
  <div class="template-header">
    <h1>${assignedTemplate.name}</h1>
    <p><strong>Note:</strong> This is a blank template with placeholders. Fill in your information when submitting the contract.</p>
  </div>
  <div class="content">
    ${assignedTemplate.content}
  </div>
</body>
</html>`;
      
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${assignedTemplate.name.replace(/[^a-z0-9]/gi, '_')}_template.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Template downloaded! Review it before filling your information.');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const handleContractSubmit = async () => {
    if (!signatureType || !signatureData) {
      toast.error('Please provide your signature');
      return;
    }
    if (!gdprAccepted) {
      toast.error('Please accept the GDPR terms');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/contract-submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_id: assignedTemplate?.id,
          apartment_name: user.apartments?.[0] || null,
          form_data: formData,
          signature_type: signatureType,
          signature_data: signatureData,
          gdpr_accepted: gdprAccepted,
          language: language
        })
      });

      if (response.ok) {
        toast.success('Contract submitted successfully! You will receive a copy via email.');
        setShowContractDialog(false);
        fetchSubmittedContracts();
        // Reset form
        setFormData({});
        setSignatureType(null);
        setSignatureData(null);
        setGdprAccepted(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit contract');
      }
    } catch (error) {
      console.error('Error submitting contract:', error);
      toast.error('Failed to submit contract');
    } finally {
      setSubmitting(false);
    }
  };

  const getMonthName = (month) => {
    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    return t(monthKeys[month - 1]);
  };

  // Filter files based on selected year only
  const filteredFiles = files.filter(file => {
    const yearMatch = filterYear === "0" || file.year === parseInt(filterYear);
    return yearMatch;
  });

  // Calculate total client net income for CURRENT YEAR ONLY (not filtered)
  const currentYear = new Date().getFullYear();
  const currentYearIncome = files
    .filter(file => file.year === currentYear)
    .reduce((sum, file) => {
      return sum + (file.client_net_income || 0);
    }, 0);

  // Group filtered files by year
  const filesByYear = filteredFiles.reduce((acc, file) => {
    if (!acc[file.year]) {
      acc[file.year] = [];
    }
    acc[file.year].push(file);
    return acc;
  }, {});

  const sortedYears = Object.keys(filesByYear).sort((a, b) => b - a);
  
  // Get unique years and months for filter dropdowns
  const availableYears = [...new Set(files.map(f => f.year))].sort((a, b) => b - a);
  const availableMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">{t('loading')}</p>
      </div>
    );
  }

  // Show profile page if requested
  if (showProfile) {
    return (
      <ClientProfile 
        user={user} 
        onBack={() => setShowProfile(false)} 
        getToken={getToken}
        language={language}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={language === 'he' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_owner-portal-3/artifacts/9p342eak_deltakey-logo-full%20%284%29.png" 
                alt="Delta Key" 
                className="h-8 sm:h-10"
              />
              <div className="hidden sm:block">
                <p className="text-sm text-gray-500">{t('propertyManagement')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <div className={`${language === 'he' ? 'text-left' : 'text-right'} hidden sm:block`}>
                <p className="text-sm font-medium text-gray-900">{user.username}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-24 sm:w-32 flex-shrink-0">
                  <Languages className="w-4 h-4 mr-1 sm:mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="el">Ελληνικά</SelectItem>
                  <SelectItem value="he">עברית</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() => setShowProfile(true)}
                variant="outline"
                size="sm"
                className="border-gray-300 hover:bg-gray-100 flex-shrink-0"
              >
                <Settings className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('profile')}</span>
              </Button>
              <Button
                data-testid="client-logout-button"
                onClick={onLogout}
                variant="outline"
                size="sm"
                className="border-gray-300 hover:bg-gray-100 flex-shrink-0"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contract Notification Banner */}
        {assignedTemplate && submittedContracts.length === 0 && (
          <Card className="mb-6 border-orange-300 bg-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-orange-900 mb-2">
                    {t('contractDetailsRequired')}
                  </h3>
                  <p className="text-orange-800 mb-4">
                    {t('completeContractPrompt')}
                  </p>
                  <Button
                    onClick={() => setShowContractDialog(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <FileSignature className="w-4 h-4 mr-2" />
                    {t('completeContractNow')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {t('yourMonthlyReports')}
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              {t('supportiveDocuments')}
            </TabsTrigger>
          </TabsList>

          {/* Monthly Reports Tab */}
          <TabsContent value="reports">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('yourMonthlyReports')}</h2>
              <p className="text-gray-600">{t('accessReports')}</p>
            </div>

        {/* Income Summary */}
        {!loading && files.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">{t('yourIncome')}</p>
                  <p className="text-3xl font-bold text-green-700">
                    €{currentYearIncome.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {currentYear}
                  </p>
                </div>
                <div className="bg-green-100 p-4 rounded-full">
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        {!loading && files.length > 0 && (
          <div className="mb-6">
            <div className="max-w-xs">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('filterByYear')}</label>
              <Select
                value={filterYear}
                onValueChange={(value) => setFilterYear(value)}
              >
                <SelectTrigger className="w-full bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">{t('allYears')}</SelectItem>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {loading ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="text-center py-12">
              <p className="text-gray-600">{t('loading')}</p>
            </CardContent>
          </Card>
        ) : files.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">{t('noFiles')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {sortedYears.map((year) => (
              <Card key={year} className="bg-white border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900">{year}</CardTitle>
                  <CardDescription>
                    {filesByYear[year].length} {filesByYear[year].length === 1 ? t('report') : t('reports')} {t('available')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full space-y-3">
                    {filesByYear[year]
                      .sort((a, b) => b.month - a.month)
                      .map((file) => (
                        <AccordionItem
                          key={file.id}
                          value={file.id}
                          className="border border-gray-200 rounded-lg px-4 bg-white hover:shadow-md transition-shadow"
                          data-testid={`file-accordion-${file.year}-${file.month}`}
                        >
                          <AccordionTrigger className="hover:no-underline py-4">
                            <div className="flex items-center justify-between w-full pr-4">
                              <div className="flex items-center space-x-4">
                                <FileText className="w-5 h-5 text-gray-600" />
                                <div>
                                  {file.client_net_income !== null && file.client_net_income !== undefined && (
                                    <span className="text-sm text-green-600 font-medium block mb-1">
                                      {t('netIncome')}: €{file.client_net_income.toFixed(2)}
                                    </span>
                                  )}
                                  <span className="font-semibold text-gray-900 block">
                                    {getMonthName(file.month)} {file.year}
                                  </span>
                                </div>
                              </div>
                              <span
                                data-testid={`payment-status-${file.year}-${file.month}`}
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  file.payment_status
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}
                              >
                                {file.payment_status ? t('paid') : t('paymentPending')}
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pt-4 pb-6 space-y-4">
                            {/* Log: Last create/edit */}
                            <p className="text-xs text-gray-500 italic">
                              {file.last_modified_at 
                                ? `${t('lastUpload')}: ${new Date(file.last_modified_at).toLocaleString()} by ${file.last_modified_by || 'Unknown'}`
                                : `${t('lastUpload')}: ${new Date(file.uploaded_at).toLocaleString()} by ${file.uploaded_by_username || 'Unknown'}`
                              }
                            </p>
                            
                            {/* Admin Message */}
                            {file.admin_message && (
                              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-semibold text-yellow-800 mb-1">{t('importantNotice')}</p>
                                    <p className="text-sm text-yellow-700">{file.admin_message}</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* File Download */}
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <p className="text-sm font-medium text-gray-700 mb-3">{t('reservationReport')}</p>
                              <a
                                href={`${process.env.REACT_APP_BACKEND_URL}${file.file_url}`}
                                download
                                data-testid={`download-file-${file.year}-${file.month}`}
                              >
                                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                                  <Download className="w-4 h-4 mr-2" />
                                  {t('downloadReport')} ({file.filename})
                                </Button>
                              </a>
                            </div>

                            {/* Documents for LTD clients */}
                            {file.company_type === 'LTD' && (file.takk_pdf_url || file.invoice_pdf_url) && (
                              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <p className="text-sm font-semibold text-blue-900 mb-3">{t('ltdDocuments')}</p>
                                <div className="space-y-3">
                                  {file.takk_pdf_url && (
                                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-gray-800">{t('takkPdf')}</p>
                                        {file.takk_paid_status && (
                                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                            Paid {file.takk_paid_timestamp && `- ${new Date(file.takk_paid_timestamp).toLocaleDateString()}`}
                                          </span>
                                        )}
                                      </div>
                                      <div className="space-y-2">
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${file.takk_pdf_url}`}
                                          download
                                        >
                                          <Button variant="outline" className="w-full">
                                            <Download className="w-4 h-4 mr-2" />
                                            {t('downloadTakk')}
                                          </Button>
                                        </a>
                                        {!file.takk_paid_status && (
                                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                            <input
                                              type="checkbox"
                                              id={`takk-paid-${file.id}`}
                                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                              onChange={async (e) => {
                                                if (e.target.checked) {
                                                  try {
                                                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/files/${file.id}/takk-payment`, {
                                                      method: 'PATCH',
                                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                    });
                                                    if (response.ok) {
                                                      toast.success(t('takkMarkedPaid'));
                                                      fetchFiles();
                                                    } else {
                                                      e.target.checked = false;
                                                      toast.error(t('failedMarkPaid'));
                                                    }
                                                  } catch (error) {
                                                    e.target.checked = false;
                                                    toast.error(t('failedMarkPaid'));
                                                  }
                                                }
                                              }}
                                            />
                                            <label htmlFor={`takk-paid-${file.id}`} className="text-sm text-gray-700 cursor-pointer">
                                              {t('markAsPaid')}
                                            </label>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {file.invoice_pdf_url && (
                                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-gray-800">{t('invoicePdf')}</p>
                                        {file.invoice_paid_status && (
                                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                            Paid {file.invoice_paid_timestamp && `- ${new Date(file.invoice_paid_timestamp).toLocaleDateString()}`}
                                          </span>
                                        )}
                                      </div>
                                      <div className="space-y-2">
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${file.invoice_pdf_url}`}
                                          download
                                        >
                                          <Button variant="outline" className="w-full">
                                            <Download className="w-4 h-4 mr-2" />
                                            {t('downloadInvoice')}
                                          </Button>
                                        </a>
                                        {!file.invoice_paid_status && (
                                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                            <input
                                              type="checkbox"
                                              id={`invoice-paid-${file.id}`}
                                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                              onChange={async (e) => {
                                                if (e.target.checked) {
                                                  try {
                                                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/files/${file.id}/invoice-payment`, {
                                                      method: 'PATCH',
                                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                    });
                                                    if (response.ok) {
                                                      toast.success(t('invoiceMarkedPaid'));
                                                      fetchFiles();
                                                    } else {
                                                      e.target.checked = false;
                                                      toast.error(t('failedMarkPaid'));
                                                    }
                                                  } catch (error) {
                                                    e.target.checked = false;
                                                    toast.error(t('failedMarkPaid'));
                                                  }
                                                }
                                              }}
                                            />
                                            <label htmlFor={`invoice-paid-${file.id}`} className="text-sm text-gray-700 cursor-pointer">
                                              {t('markAsPaid')}
                                            </label>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Documents for LP clients with VAT */}
                            {file.company_type === 'LP' && file.invoice_pdf_url && (
                              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <p className="text-sm font-semibold text-blue-900 mb-3">{t('lpDocuments')}</p>
                                <div className="space-y-3">
                                  {file.invoice_pdf_url && (
                                    <div className="bg-white p-3 rounded-lg border border-blue-200">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-gray-800">{t('invoicePdf')}</p>
                                        {file.invoice_paid_status && (
                                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                            Paid {file.invoice_paid_timestamp && `- ${new Date(file.invoice_paid_timestamp).toLocaleDateString()}`}
                                          </span>
                                        )}
                                      </div>
                                      <div className="space-y-2">
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${file.invoice_pdf_url}`}
                                          download
                                        >
                                          <Button variant="outline" className="w-full">
                                            <Download className="w-4 h-4 mr-2" />
                                            {t('downloadInvoice')}
                                          </Button>
                                        </a>
                                        {!file.invoice_paid_status && (
                                          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                            <input
                                              type="checkbox"
                                              id={`invoice-paid-lp-${file.id}`}
                                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                                              onChange={async (e) => {
                                                if (e.target.checked) {
                                                  try {
                                                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client/files/${file.id}/invoice-payment`, {
                                                      method: 'PATCH',
                                                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                                    });
                                                    if (response.ok) {
                                                      toast.success(t('invoiceMarkedPaid'));
                                                      fetchFiles();
                                                    } else {
                                                      e.target.checked = false;
                                                      toast.error(t('failedMarkPaid'));
                                                    }
                                                  } catch (error) {
                                                    e.target.checked = false;
                                                    toast.error(t('failedMarkPaid'));
                                                  }
                                                }
                                              }}
                                            />
                                            <label htmlFor={`invoice-paid-lp-${file.id}`} className="text-sm text-gray-700 cursor-pointer">
                                              {t('markAsPaid')}
                                            </label>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Other Documents */}
                            {file.additional_files && file.additional_files.length > 0 && (
                              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <p className="text-sm font-semibold text-green-900 mb-3">{t('otherDocuments')}</p>
                                <div className="space-y-2">
                                  {file.additional_files.map((addFile, idx) => (
                                    <div key={idx} className="bg-white p-3 rounded-lg border border-green-200">
                                      <div className="flex items-center justify-between">
                                        <p className="text-sm text-gray-800">{addFile.filename}</p>
                                        <a
                                          href={`${process.env.REACT_APP_BACKEND_URL}${addFile.file_url}`}
                                          download
                                        >
                                          <Button variant="outline" size="sm">
                                            <Download className="w-3 h-3 mr-1" />
                                            {t('downloadDocument')}
                                          </Button>
                                        </a>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Payment Information - Based on Company Type */}
                            {!file.payment_status && paymentSettings && (
                              <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
                                <p className="text-sm font-semibold text-gray-800 mb-3">{t('paymentInformation')}</p>
                                <div className="space-y-3">
                                  {/* Stripe Link for LTD */}
                                  {paymentSettings.stripe_link && (
                                    <>
                                      <a
                                        href={paymentSettings.stripe_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`pay-now-stripe-${file.year}-${file.month}`}
                                      >
                                        <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                                          <CreditCard className="w-4 h-4 mr-2" />
                                          {t('payNowWithStripe')}
                                        </Button>
                                      </a>
                                      {(paymentSettings.iban || paymentSettings.wise_account) && (
                                        <div className="flex items-center justify-center py-1">
                                          <span className="text-xs text-gray-500 font-medium">{t('or')}</span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* IBAN (for both LTD and LP) */}
                                  {paymentSettings.iban && (
                                    <>
                                      <div className="bg-white p-3 rounded-lg border border-gray-200 space-y-2">
                                        <div className="flex items-start space-x-2">
                                          <Building className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-gray-700 mb-2">{t('bankTransferIban')}</p>
                                            
                                            {/* IBAN */}
                                            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                                              <div className="flex-1 min-w-0 mr-2">
                                                <p className="text-xs text-gray-500">IBAN</p>
                                                <p className="text-sm text-gray-800 font-mono break-all">{paymentSettings.iban}</p>
                                              </div>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => copyToClipboard(paymentSettings.iban, `iban-${file.id}`)}
                                                className="flex-shrink-0"
                                              >
                                                {copiedField === `iban-${file.id}` ? (
                                                  <Check className="w-4 h-4 text-green-600" />
                                                ) : (
                                                  <Copy className="w-4 h-4" />
                                                )}
                                              </Button>
                                            </div>
                                            
                                            {/* Beneficiary */}
                                            {paymentSettings.iban_beneficiary && (
                                              <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                                                <div className="flex-1 min-w-0 mr-2">
                                                  <p className="text-xs text-gray-500">{t('beneficiary')}</p>
                                                  <p className="text-sm text-gray-800 break-all">{paymentSettings.iban_beneficiary}</p>
                                                </div>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => copyToClipboard(paymentSettings.iban_beneficiary, `beneficiary-${file.id}`)}
                                                  className="flex-shrink-0"
                                                >
                                                  {copiedField === `beneficiary-${file.id}` ? (
                                                    <Check className="w-4 h-4 text-green-600" />
                                                  ) : (
                                                    <Copy className="w-4 h-4" />
                                                  )}
                                                </Button>
                                              </div>
                                            )}
                                            
                                            {/* Bank Name */}
                                            {paymentSettings.iban_bank_name && (
                                              <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-100">
                                                <div className="flex-1 min-w-0 mr-2">
                                                  <p className="text-xs text-gray-500">{t('bankName')}</p>
                                                  <p className="text-sm text-gray-800 break-all">{paymentSettings.iban_bank_name}</p>
                                                </div>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => copyToClipboard(paymentSettings.iban_bank_name, `bankname-${file.id}`)}
                                                  className="flex-shrink-0"
                                                >
                                                  {copiedField === `bankname-${file.id}` ? (
                                                    <Check className="w-4 h-4 text-green-600" />
                                                  ) : (
                                                    <Copy className="w-4 h-4" />
                                                  )}
                                                </Button>
                                              </div>
                                            )}
                                            
                                            {/* BIC */}
                                            {paymentSettings.iban_bic && (
                                              <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0 mr-2">
                                                  <p className="text-xs text-gray-500">{t('bic')}</p>
                                                  <p className="text-sm text-gray-800 font-mono break-all">{paymentSettings.iban_bic}</p>
                                                </div>
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() => copyToClipboard(paymentSettings.iban_bic, `bic-${file.id}`)}
                                                  className="flex-shrink-0"
                                                >
                                                  {copiedField === `bic-${file.id}` ? (
                                                    <Check className="w-4 h-4 text-green-600" />
                                                  ) : (
                                                    <Copy className="w-4 h-4" />
                                                  )}
                                                </Button>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      {(paymentSettings.revolut_tag || paymentSettings.wise_account) && (
                                        <div className="flex items-center justify-center py-1">
                                          <span className="text-xs text-gray-500 font-medium">{t('or')}</span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* Revolut for LP */}
                                  {paymentSettings.revolut_tag && (
                                    <>
                                      <div className="bg-white p-3 rounded-lg border border-gray-200">
                                        <div className="flex items-start justify-between">
                                          <div className="flex items-start space-x-2 flex-1">
                                            <Wallet className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-medium text-gray-600 mb-1">Revolut</p>
                                              <p className="text-sm text-gray-800 font-mono break-all" data-testid={`revolut-tag-${file.year}-${file.month}`}>
                                                {paymentSettings.revolut_tag}
                                              </p>
                                            </div>
                                          </div>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => copyToClipboard(paymentSettings.revolut_tag, `revolut-${file.id}`)}
                                            className="ml-2 flex-shrink-0"
                                          >
                                            {copiedField === `revolut-${file.id}` ? (
                                              <Check className="w-4 h-4 text-green-600" />
                                            ) : (
                                              <Copy className="w-4 h-4" />
                                            )}
                                          </Button>
                                        </div>
                                      </div>
                                      {paymentSettings.wise_account && (
                                        <div className="flex items-center justify-center py-1">
                                          <span className="text-xs text-gray-500 font-medium">{t('or')}</span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                  
                                  {/* Wise for LTD */}
                                  {paymentSettings.wise_account && (
                                    <div className="bg-white p-3 rounded-lg border border-gray-200">
                                      <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-2 flex-1">
                                          <Wallet className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-600 mb-1">{t('wiseTransfer')}</p>
                                            <p className="text-sm text-gray-800 font-mono break-all" data-testid={`wise-account-${file.year}-${file.month}`}>
                                              {paymentSettings.wise_account}
                                            </p>
                                          </div>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => copyToClipboard(paymentSettings.wise_account, `wise-${file.id}`)}
                                          className="ml-2 flex-shrink-0"
                                        >
                                          {copiedField === `wise-${file.id}` ? (
                                            <Check className="w-4 h-4 text-green-600" />
                                          ) : (
                                            <Copy className="w-4 h-4" />
                                          )}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {file.payment_status && (
                              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <p className="text-sm text-green-700 font-medium text-center">
                                  ✓ {t('paymentReceived')}
                                </p>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          {/* Supportive Documents Tab */}
          <TabsContent value="documents">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('supportiveDocuments')}</h2>
              <p className="text-gray-600">{t('accessHelpfulResources')}</p>
            </div>
            
            <Card className="bg-white border-gray-200">
              <CardContent className="pt-6">
              {loadingTopics ? (
                <div className="text-center py-8 text-gray-500">{t('loadingDocuments')}</div>
              ) : supportiveTopics.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">{t('noDocumentsAvailable')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {supportiveTopics.map((topic) => (
                    <Card key={topic.id} className="border-l-4 border-l-blue-500">
                      <CardHeader>
                        <CardTitle className="text-lg">{topic.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Description */}
                        <div 
                          className="prose prose-sm max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: topic.description }}
                        />
                        
                        {/* Files List */}
                        {topic.files && topic.files.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">{t('availableFiles')}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {topic.files.map((file) => (
                                <div
                                  key={file.id}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <span className="text-2xl flex-shrink-0">{getFileIcon(file.file_type)}</span>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {file.file_name}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {(file.file_size / 1024 / 1024).toFixed(2)} MB • {file.file_type?.toUpperCase()}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => downloadSupportiveFile(file.id, file.file_name)}
                                    className="ml-2 flex-shrink-0"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Contract Form Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('completeYourContract')}</DialogTitle>
            <DialogDescription>
              {t('fillDetailsAndSign')}
            </DialogDescription>
          </DialogHeader>
          
          {assignedTemplate && (
            <div className="space-y-6 pb-4">
              {/* 1. Contract Template with Placeholders (NOT filled) */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-blue-900">{assignedTemplate.name}</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={downloadEmptyTemplate}
                    className="bg-white"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {t('downloadEmptyTemplate')}
                  </Button>
                </div>
                <div className="prose prose-sm max-w-none text-gray-700 max-h-64 overflow-y-auto bg-white p-4 rounded border border-blue-200">
                  <div dangerouslySetInnerHTML={{ __html: assignedTemplate.content }} />
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  ℹ️ Download the template to review it. Fill in your information below to replace the placeholders like {'{FirstName}'}, {'{LastName}'}, etc.
                </p>
              </div>
              
              {/* 3. Your Information Section - Dynamic fields from placeholders */}
              <div className="space-y-4 border-t pt-6">
                <h4 className="font-semibold text-gray-900">Your Information</h4>
                <p className="text-sm text-gray-600">Fill in the information below. These values will replace the placeholders in your contract.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {extractPlaceholders(assignedTemplate.content).map((placeholder) => (
                    <div key={placeholder} className={placeholder.toLowerCase().includes('address') ? 'md:col-span-2' : ''}>
                      <Label htmlFor={placeholder}>
                        {placeholder.replace(/([A-Z])/g, ' $1').trim()} *
                      </Label>
                      <Input
                        id={placeholder}
                        value={formData[placeholder] || ''}
                        onChange={(e) => setFormData({ ...formData, [placeholder]: e.target.value })}
                        required
                        placeholder={`Enter ${placeholder.replace(/([A-Z])/g, ' $1').trim()}`}
                      />
                    </div>
                  ))}
                </div>
                {extractPlaceholders(assignedTemplate.content).length === 0 && (
                  <p className="text-sm text-gray-500 italic">No placeholders found in template.</p>
                )}
              </div>
              
              {/* Signature */}
              <div className="border-t pt-6">
                <SignatureCapture
                  onSignatureChange={(type, data) => {
                    setSignatureType(type);
                    setSignatureData(data);
                  }}
                  language={language}
                />
              </div>
              
              {/* GDPR Acceptance */}
              <div className="border-t pt-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="gdpr-accept"
                    checked={gdprAccepted}
                    onChange={(e) => setGdprAccepted(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded mt-1"
                  />
                  <Label htmlFor="gdpr-accept" className="cursor-pointer text-sm text-gray-700">
                    I accept the terms and conditions. I understand that my personal data will be processed in accordance with GDPR regulations for the purpose of this rental agreement. A copy of the signed contract will be sent to my email address.
                  </Label>
                </div>
              </div>
              
              {/* Submit Button */}
              <Button
                onClick={handleContractSubmit}
                disabled={submitting || !signatureType || !signatureData || !gdprAccepted}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                {submitting ? t('submitting') : t('submitContract')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
